import { defineStore } from 'pinia'

import { Mix } from '../models/Mix.js'
import type { Ingredient } from '@/types/ingredient'

interface IMix {
  editedMix: Mix | null
  mix: Mix | null
}

const allowKinds = new Set(['musli', 'porridge', 'dry_fruits', 'crunchy_fruits', 'mixit_bars', 'fruit_bags'])

export const useMixStore = defineStore('mixStore', {
  state: (): IMix => ({
    editedMix: null,
    mix: null,
  }),

  actions: {
    addItem(params: { kind: string, ingredient: Ingredient, quantity: number}) {
      const { kind, ingredient, quantity } = params
      const mix = this.getMix(kind)!
      const item = {
        ingredient,
        amount: quantity,
      }
      mix.items.push(item)
    },
    removeItem(params: { kind: string, ingredient: Ingredient }) {
      const mix = this.getMix(params.kind)!
      mix.items = mix.items.filter((item) => {
        return item.ingredient.id !== params.ingredient.id
      })
    },
    increaseQuantity(params: {kind: string, ingredient: Ingredient, quantity: number}) {
      const { kind, ingredient, quantity } = params
      const mix = this.getMix(kind)!
      const item = mix.items.find((item) => {
        return item.ingredient.id === ingredient.id
      })!
      item.amount += quantity
    },
    decreaseQuantity(params: {kind: string, ingredient: Ingredient, quantity: number}) {
      const { kind, ingredient, quantity } = params
      const mix = this.getMix(kind)!
      const item = mix.items.find((item) => {
        return item.ingredient.id === ingredient.id
      })!
      item.amount -= quantity
    },
    changeBase(params: {kind: string, ingredient: Ingredient}) {
      const mix = this.getMix(params.kind)!
      if (!mix.hasBase) return
      const item = {
        ingredient: params.ingredient,
        amount: 1,
      }
      mix.items = mix.items.filter((i) => {
        return i.ingredient.id !== mix.base.ingredient.id
      })
      mix.items.push(item)
    },
    resetMix(kind: string) {
      this.mix = new Mix(kind)
    },
    editMix(mix: Mix) {
      // Resets currently edited mix, if editedMix exists.
      if (this.editedMix) {
        this.resetMix(this.editedMix.kind)
      }

      this.editedMix = mix
    },
    cancelEditMix(kind: string) {
      this.resetMix(kind)
      this.editedMix = null
    },
    getMix(kind: string) {
      if (this.mix?.kind === kind) {
        return this.mix
      }

      if (allowKinds.has(kind)) {
        const newMix = new Mix(kind)
        this.editedMix = null
        this.mix = newMix
        return newMix
      }

      return null
    },
  },

  getters: {
    getEditedMix: (state) => state.editedMix,
    getActualMix: (state) => state.mix,
    getMixItems: (state) => state.mix?.items,
    getMixLength: (state) => state.mix?.items?.length,
  },
})
